/**
 * Hook owning the viewer's reactive state: which page is current, zoom
 * level, fit mode, and rotation.
 *
 * Kept dependency-free (no React refs to the DOM) so it can be unit-tested
 * with renderHook + jsdom without mocking pdfjs.
 */
import { useCallback, useMemo, useState } from 'react'

import { clampScale, computeFitScale } from './coordinates'
import type {
  FitMode,
  PageModel,
  Rotation,
  ViewerState,
} from './types'

interface UseViewerStateArgs {
  pages: PageModel[]
  initialFitMode?: FitMode
  initialPageIndex?: number
  initialScale?: number
  initialRotation?: Rotation
}

export interface UseViewerStateApi {
  state: ViewerState
  setCurrentPageIndex: (index: number) => void
  goToPage: (pageNumber: number) => void
  goNext: () => void
  goPrev: () => void
  zoomIn: () => void
  zoomOut: () => void
  setScale: (scale: number) => void
  setFitMode: (mode: FitMode) => void
  setRotation: (r: Rotation) => void
  /**
   * Recompute the rendered scale when fitMode != custom. The component
   * calls this whenever the viewport size changes.
   */
  applyFit: (viewport: { width: number; height: number }) => void
}

const ZOOM_STEPS = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.85, 1, 1.15, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 6,
]

function nextZoom(current: number): number {
  for (const step of ZOOM_STEPS) {
    if (step > current + 1e-3) return step
  }
  return ZOOM_STEPS[ZOOM_STEPS.length - 1]!
}

function prevZoom(current: number): number {
  for (let i = ZOOM_STEPS.length - 1; i >= 0; i--) {
    const step = ZOOM_STEPS[i]!
    if (step < current - 1e-3) return step
  }
  return ZOOM_STEPS[0]!
}

export function useViewerState(args: UseViewerStateArgs): UseViewerStateApi {
  const {
    pages,
    initialFitMode = 'width',
    initialPageIndex = 0,
    initialScale = 1,
    initialRotation = 0,
  } = args

  const [state, setState] = useState<ViewerState>(() => ({
    currentPageIndex: initialPageIndex,
    scale: initialScale,
    fitMode: initialFitMode,
    rotation: initialRotation,
  }))

  const clampPageIndex = useCallback(
    (i: number): number => {
      const len = pages.length
      if (len === 0) return 0
      if (i < 0) return 0
      if (i >= len) return len - 1
      return i
    },
    [pages],
  )

  const setCurrentPageIndex = useCallback(
    (index: number) => {
      setState((s) => {
        const next = clampPageIndex(index)
        if (next === s.currentPageIndex) return s
        return { ...s, currentPageIndex: next }
      })
    },
    [clampPageIndex],
  )

  const goToPage = useCallback(
    (pageNumber: number) => setCurrentPageIndex(pageNumber - 1),
    [setCurrentPageIndex],
  )

  const goNext = useCallback(() => {
    setState((s) => ({
      ...s,
      currentPageIndex: clampPageIndex(s.currentPageIndex + 1),
    }))
  }, [clampPageIndex])

  const goPrev = useCallback(() => {
    setState((s) => ({
      ...s,
      currentPageIndex: clampPageIndex(s.currentPageIndex - 1),
    }))
  }, [clampPageIndex])

  const zoomIn = useCallback(() => {
    setState((s) => ({
      ...s,
      scale: clampScale(nextZoom(s.scale)),
      fitMode: 'custom',
    }))
  }, [])

  const zoomOut = useCallback(() => {
    setState((s) => ({
      ...s,
      scale: clampScale(prevZoom(s.scale)),
      fitMode: 'custom',
    }))
  }, [])

  const setScale = useCallback((scale: number) => {
    setState((s) => ({ ...s, scale: clampScale(scale), fitMode: 'custom' }))
  }, [])

  const setFitMode = useCallback((mode: FitMode) => {
    setState((s) => ({ ...s, fitMode: mode }))
  }, [])

  const setRotation = useCallback((r: Rotation) => {
    setState((s) => ({ ...s, rotation: r }))
  }, [])

  const applyFit = useCallback(
    (viewport: { width: number; height: number }) => {
      setState((s) => {
        if (s.fitMode === 'custom') return s
        const page = pages[s.currentPageIndex] ?? pages[0]
        if (!page) return s
        const fit = computeFitScale({
          dims: page.intrinsic,
          rotation: s.rotation,
          viewport,
          mode: s.fitMode,
        })
        const next = clampScale(fit)
        if (Math.abs(next - s.scale) < 1e-4) return s
        return { ...s, scale: next }
      })
    },
    [pages],
  )

  return useMemo(
    () => ({
      state,
      setCurrentPageIndex,
      goToPage,
      goNext,
      goPrev,
      zoomIn,
      zoomOut,
      setScale,
      setFitMode,
      setRotation,
      applyFit,
    }),
    [
      state,
      setCurrentPageIndex,
      goToPage,
      goNext,
      goPrev,
      zoomIn,
      zoomOut,
      setScale,
      setFitMode,
      setRotation,
      applyFit,
    ],
  )
}
